import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Typography2 } from '@coursera/cds-core';
import { StyleSheet, css } from '@coursera/coursera-ui';

import type { SubscriptionTiersProductVariant } from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import { isMonthlyByVariantId as isMonthly } from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import { ERROR_MESSAGES } from 'bundles/coursera-plus-landing-page/components/ContentfulError';
import type { ErrorMessageFunction } from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentfulPiece';
import PricedPretranslated from 'bundles/coursera-plus-landing-page/components/ctaContentful/PricedPretranslated';
import { DISCLAIMER_LINKS } from 'bundles/payments-common/common/constants';

import _t from 'i18n!nls/coursera-plus-landing-page';

const getTermsOfUseLink = (message?: string) => {
  const termsOfUseLink = (
    <Link className="cplus-footer-link" href={DISCLAIMER_LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
      {_t('Terms')}
    </Link>
  );
  return <FormattedMessage message={message ?? _t('{termsLink} apply.')} termsLink={termsOfUseLink} />;
};

const getFormattedSurveyedResponsesCopy = () => {
  return (
    <FormattedMessage
      message={_t(
        "{joinSurveyedLearners}{superScriptCitation} You'll gain the freedom to explore skills in careers like UX design, data analytics, social media marketing, and more. Open up your options—and your future—with the flexibility to learn the tools you need to take control of your career today."
      )}
      joinSurveyedLearners={
        <strong>
          {_t(
            'Join the 77% of surveyed learners on Coursera who have reported career benefits including new jobs, promotions, and expanded skill sets.'
          )}
        </strong>
      }
      superScriptCitation={<sup>{_t('1')}</sup>}
    />
  );
};

const getFormattedSurveyedResponsesCopyVersion2 = () => {
  return (
    <FormattedMessage
      message={_t(
        'Join the 77% of learners who reported career benefits, including a new job, promotion, and applicable skills.{superScriptCitation}'
      )}
      superScriptCitation={<sup>{_t('1')}</sup>}
    />
  );
};

const getFormattedSurveyedResponsesCopyQ32023 = () => {
  return (
    <FormattedMessage
      message={_t(
        "{joinSurveyedLearners}{superScriptCitation} You'll gain the freedom to explore skills in careers like cybersecurity, data analytics, and more."
      )}
      joinSurveyedLearners={
        <strong>
          {_t(
            'Join the 95% of learners on Coursera who have reported personal benefits including a boost in confidence, a feeling of accomplishment, and more.'
          )}
        </strong>
      }
      superScriptCitation={<sup>{_t('1')}</sup>}
    />
  );
};

const getFormattedSurveyedResponsesCopyJoin = (percentage: number) => {
  return (
    <FormattedMessage
      message={_t(
        'Join the {percentage}% of learners who reported career benefits, including a new job, promotion, and applicable skills.{superScriptCitation}'
      )}
      superScriptCitation={<sup>{_t('1')}</sup>}
      percentage={percentage}
    />
  );
};

const getLearnerFirstJobMotivationCopyQ32023 = () => {
  return (
    <FormattedMessage
      message={_t(
        'Looking to land your first job, switch to a new career, or advance in your current role? Look no further. With Coursera Plus you can access 6,100 courses and learn job-ready skills from institutions like Google, Microsoft, and Yale, for one all-inclusive price. Join the ranks of 1 in 4 learners who completed a Professional Certificate and got a new job {superScriptCitation}'
      )}
      superScriptCitation={<sup>{_t('1')}</sup>}
    />
  );
};

const getCourseraLearnerOutcomesReport = () => {
  return (
    <Link
      href="https://about.coursera.org/press/wp-content/uploads/2023/05/Learner-Outcomes-Report-2023.pdf"
      target="_blank"
      rel="noopener noreferrer"
      className="cplus-footer-link"
    >
      <FormattedMessage
        message={_t('{superScriptCitation} 2023 Coursera Learner Outcomes Report')}
        superScriptCitation={<sup>{_t('1')}</sup>}
      />
    </Link>
  );
};

const getFormattedAboutArchiCopy = () => {
  return (
    <div>
      <Typography2 component="p" variant="subtitleMedium">
        {_t('About Archi:')}
      </Typography2>
      <ul>
        <li>
          <Typography2 component="span">{_t('18 years of successful experience')}</Typography2>
        </li>
        <li>
          <Typography2 component="span">{_t('49 completed projects, covering 1,058,723 sq.m')}</Typography2>
        </li>
        <li>
          <Typography2 component="span">{_t('25 ongoing projects, covering 841,074 sq.m')}</Typography2>
        </li>
        <li>
          <Typography2 component="span">{_t('More than 36,000 residents have chosen Archi as their home')}</Typography2>
        </li>
      </ul>
    </div>
  );
};

export const VALUE_TYPES = {
  price: 'price',
  termsOfUseLink: 'termsOfUseLink',
  termsOfUseForPromosLink: 'termsOfUseForPromosLink',
  formattedSurveyedResponsesCopy: 'formattedSurveyedResponsesCopy',
  formattedSurveyedResponsesCopyQ32023: 'formattedSurveyedResponsesCopyQ32023',
  formattedSurveyedResponsesJoin89Percent: 'formattedSurveyedResponsesJoin89Percent',
  formattedSurveyedResponsesJoin91Percent: 'formattedSurveyedResponsesJoin91Percent',
  courseraLearnerOutcomesReport: 'courseraLearnerOutcomesReport',
  learnerFirstJobMotivation: 'learnerFirstJobMotivation',
  formattedSurveyedResponsesCopyVersion2: 'formattedSurveyedResponsesCopyVersion2',
  strikethroughValue: 'strikethroughValue',
  aboutArchi: 'aboutArchi',
  socialProofDeutchCopy: 'socialProofDeutchCopy',
  socialProofLatamCopy: 'socialProofLatamCopy',
  socialProofFrenchCopy: 'socialProofFrenchCopy',
};

const styles = StyleSheet.create({
  blockedDiscount: {
    textDecoration: 'line-through',
    opacity: 0.55,
  },
  smaller: {
    fontSize: '15px',
  },
});

export type PretranslatedType = {
  message: string;
  valueType: string;
  blockDiscount?: boolean /* for the original price when the actual price is discounted */;
  showDiscountAmount?: boolean /* display diff between discounted and original */;
  priceAndSaving?: boolean;
  className?: string;
  termsOfUseLink?: JSX.Element;

  // This is a fairly specific case. Basically, we divide
  // the yearly price by 12 months and round it up to the nearest multiple
  // of the value stated here
  monthlyFromAnnualRoundedToNearestValue?: number;
};

const getFormattedSurveyedResponsesCustomLanguage = (valueType: string) => {
  if (valueType === VALUE_TYPES.socialProofFrenchCopy) {
    return (
      <FormattedMessage
        message={_t(
          "Rejoignez les 77 % d'apprenants qui ont fait avancer leur carrière, que ce soit pour obtenir un nouvel emploi, une promotion ou de nouvelles compétences.{superScriptCitation}"
        )}
        superScriptCitation={<sup>{_t('1')}</sup>}
      />
    );
  } else if (valueType === VALUE_TYPES.socialProofDeutchCopy) {
    return (
      <FormattedMessage
        message={_t(
          'Folge dem Beispiel von 77 % der Lernenden, die von Vorteilen für ihre Karriere berichteten, darunter ein neuer Job, eine Beförderung und nützliche Kompetenzen.{superScriptCitation}'
        )}
        superScriptCitation={<sup>{_t('1')}</sup>}
      />
    );
  } else if (valueType === VALUE_TYPES.socialProofLatamCopy) {
    return (
      <FormattedMessage
        message={_t(
          'Únete al 77 % de estudiantes que obtuvieron beneficios profesionales como trabajos nuevos, ascensos y habilidades aplicables.{superScriptCitation}'
        )}
        superScriptCitation={<sup>{_t('1')}</sup>}
      />
    );
  }

  return null;
};

export type PretranslatedMap = { [key: string]: PretranslatedType };

export const getPretranslatedMessages = (productItemId: $TSFixMe): PretranslatedMap => ({
  studentHeader: {
    message: 'Reach your goal with unlimited learning for only {price}',
    valueType: VALUE_TYPES.price,
  },
  studentSubHeader: {
    message: _t(
      'Get your Coursera Plus subscription for {price} and start your career with unlimited access to {numberOfProducts} courses and job-ready certificate programs.'
    ),
    valueType: VALUE_TYPES.price,
  },
  'price/time unit': {
    message: isMonthly(productItemId) ? _t('{price}/month') : _t('{price}/year'),
    valueType: VALUE_TYPES.price,
  },
  'price/time unit (undiscounted price)': {
    message: isMonthly(productItemId) ? _t('{price}/month') : _t('{price}/year'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'price/time unit, then': {
    message: isMonthly(productItemId) ? _t('{price}/month, then') : _t('{price}/year, then'),
    valueType: VALUE_TYPES.price,
  },

  'price/time unit, cancel anytime': {
    message: isMonthly(productItemId) ? _t('{price}/month, cancel anytime') : _t('{price}/year, cancel anytime'),
    valueType: VALUE_TYPES.price,
  },

  'price/time unit, cancel anytime (original price)': {
    message: isMonthly(productItemId) ? _t('{price}/month, cancel anytime') : _t('{price}/year, cancel anytime'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },

  'or price/time unit with 14-day money-back guarantee': {
    message: isMonthly(productItemId)
      ? _t('or {price}/month with 14-day money-back guarantee')
      : _t('or {price}/year with 14-day money-back guarantee'),
    valueType: VALUE_TYPES.price,
  },
  'Upgrade to Coursera Plus Annual (original price)': {
    message: isMonthly(productItemId)
      ? _t('or {price}/month with 14-day money-back guarantee')
      : _t('or {price}/year with 14-day money-back guarantee'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'price without discount, strikethrough': {
    message: isMonthly(productItemId) ? _t('or {price}/month') : _t('or {price}/year'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
    className: css(styles.blockedDiscount).className,
  },

  'undiscounted price strikethrough plain': {
    message: isMonthly(productItemId) ? _t('{price}/month') : _t('{price}/year'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
    className: css(styles.blockedDiscount).className,
  },

  'then price/time unit until you cancel': {
    message: isMonthly(productItemId)
      ? _t('then {price}/month until you cancel')
      : _t('then {price}/year until you cancel'),
    valueType: VALUE_TYPES.price,
  },

  'Two months free then price/time unit': {
    message: isMonthly(productItemId)
      ? _t('Two months free then {price}/month')
      : _t('Two months free then {price}/year'),
    valueType: VALUE_TYPES.price,
  },

  'One month free then price/month (undiscounted price)': {
    message: _t('One month free then {price}/month'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },

  'After the 30 day free trial, this subscription auto-renews monthly for price/month.': {
    message: _t('After the 30 day free trial, this subscription auto-renews monthly for {price}/month.'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'Get Plus for $1 (amount discount)': {
    message: _t('Get Plus for {price}'),
    valueType: VALUE_TYPES.price,
  },
  'Get your first month (with a friend) for $1 (discount price)': {
    message: _t('Get your first month (with a friend) for {price}'),
    valueType: VALUE_TYPES.price,
  },
  'Discount applied...renews on an annual basis for {discountedPrice}/year...': {
    message: _t(
      'Discount applied at checkout. Automatically renews on an annual basis for {price}/year (plus applicable taxes), unless canceled. Cancel anytime in account settings.'
    ),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'Savings compared to regular monthly price...': {
    message: _t(
      '*Savings compared to regular monthly price for Coursera Plus. Offer valid for one-time discount. Automatically renews on a monthly basis for {price}/mo (plus tax, where applicable), unless canceled. Promotion valid until Aug 29, 2024, 11:59 PM PT for new Coursera Plus subscribers only. Not valid in India. Offer cannot be combined with any other offers or discounts. Offer is non-transferable and not valid for cash or cash equivalent. No adjustments on previous purchases. Offer subject to change without notice.'
    ),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'Expand your career horizons with Coursera Plus for just {price}/month': {
    message: _t('Expand your career horizons with Coursera Plus for just {price}/month'),
    valueType: VALUE_TYPES.price,
    monthlyFromAnnualRoundedToNearestValue: 1,
  },
  'For a limited time, you can access Coursera Plus for only {price} a month...': {
    message: _t(
      "For a limited time, you can access Coursera Plus for only {price} a month, billed annually . That's 40% in savings!"
    ),
    valueType: VALUE_TYPES.price,
    monthlyFromAnnualRoundedToNearestValue: 1,
  },
  '$1 in the first month (discount amount)': {
    message: _t('{price} in the first month.'),
    valueType: VALUE_TYPES.price,
  },
  '$1 for first month (discount amount)': {
    message: _t('{price} for the first month'),
    valueType: VALUE_TYPES.price,
  },
  'then price/month': {
    message: _t(', then {price}/month.'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'After the first month, this subscription auto-renews monthly for price/month.': {
    message: _t('After the first month, this subscription auto-renews monthly for {price}/month.'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'Enter a world of unlimited learning for discounted price': {
    message: _t('Enter a world of unlimited learning for {price}'),
    valueType: VALUE_TYPES.price,
  },
  'Start your Coursera Plus subscription for $1': {
    message: _t(
      'Start your Coursera Plus subscription for {price} and build new career skills with unlimited access to {numberOfProducts} courses and job-ready certificate programs.'
    ),
    valueType: VALUE_TYPES.price,
  },
  'Price for the first year': {
    message: _t('{price} today,'),
    valueType: VALUE_TYPES.price,
  },
  'then price/time unit strikethrough': {
    message: isMonthly(productItemId) ? _t('then {price}/month') : _t('then {price}/year'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
    className: css(styles.smaller).className,
  },
  'Open new doors with {discountPrice} off Coursera Plus': {
    message: _t('Open new doors with {price} off Coursera Plus'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'With Coursera Plus, you will have maximum flexibility...': {
    message: _t(
      "With Coursera Plus, you'll have maximum flexibility to start new courses and move between programs at your pace—with no additional costs. And with our limited-time offer of {price} off your annual subscription, "
    ),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'you will get unlimited opportunities...': {
    message: _t(" you'll get unlimited opportunities to grow your career for less than {price}/month for one year!"),
    valueType: VALUE_TYPES.price,
    monthlyFromAnnualRoundedToNearestValue: 5,
  },
  'get $100 off': {
    message: _t('Get {price} off a year of job-ready learning with Coursera Plus'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  '$1 is all that is standing between you and the next step...': {
    message: _t(
      "{price} is all that's standing between you and the next step in your career. With this limited-time offer to Coursera Plus, you'll get unlimited access to 6,100 learning programs from world-class universities and companies like Yale, Google, Microsoft, Salesforce and others—all in one subscription."
    ),
    valueType: VALUE_TYPES.price,
  },
  'Subscribe today for {price} off': {
    message: _t('Subscribe today for {price} off!'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'Reach your goals with {price} off one year of Coursera Plus': {
    message: _t('Reach your goals with {price} off one year of Coursera Plus'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'Ready to achieve your career goals for less than {customPrice}/month?': {
    message: _t('Ready to achieve your career goals for less than {price}/month?'),
    valueType: VALUE_TYPES.price,
    monthlyFromAnnualRoundedToNearestValue: 5,
  },
  '{discountPrice} off Coursera Plus means big savings for your big goals!': {
    message: _t('{price} off Coursera Plus means big savings for your big goals!'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'Get Coursera Lite for only {regularPrice}!': {
    message: _t('Get Coursera Lite for only {price}!'),
    valueType: VALUE_TYPES.price,
  },
  '{discountPrice} for 12 months': {
    message: _t('{price} for 12 months'),
    valueType: VALUE_TYPES.price,
  },
  '(regularly {regularPrice})': {
    message: _t(' (regularly {price})'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  '(regularly {regularPrice}) (with full stop at the end of sentence)': {
    message: _t(' (regularly {price}).'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'With our limited-time offer of {discountPrice} off...': {
    message: _t(
      "With our limited-time offer of {price} off your annual subscription, you'll get unlimited access to {numberOfProducts} courses from world-class universities and companies."
    ),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'Automatically renews on annual basis for {regularPrice}/year...': {
    message: _t(
      'Automatically renews on an annual basis for {price}/year (plus applicable taxes), unless canceled. Cancel anytime in account settings.'
    ),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'Automatic annual renewal at US$ 399/year + taxes. Cancel anytime.': {
    message: _t('Automatic annual renewal at {price}/year + taxes. Cancel anytime.'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  '{discountedPrice}': {
    message: _t('{price}'),
    valueType: VALUE_TYPES.price,
  },
  'Just $1 unlocks unlimited opportunities with Coursera Plus': {
    message: _t('Just {price} unlocks unlimited opportunities with Coursera Plus'),
    valueType: VALUE_TYPES.price,
  },
  "Tentpole promo: Coursera Plus is your perfect plus one if you're looking": {
    message: _t(
      "Coursera Plus is your perfect plus one if you're looking to take the next step in your career. Enjoy your first month for just {price} with this limited-time offer and get unlimited access to {numberOfProducts} courses from world-class universities and companies like Yale, Google, Microsoft, Salesforce and others—all in one subscription."
    ),
    valueType: VALUE_TYPES.price,
  },
  'celebrate birthday with $10': {
    message: _t('Celebrate our 10th anniversary with this {price} offer!'),
    valueType: VALUE_TYPES.price,
  },
  'limited time birthday': {
    message: _t(
      'Claim this special limited-time offer by April 28, 2022. Valid for new Coursera Plus subscribers only. After the first month, this subscription auto-renews monthly for {price}, with the option to cancel anytime.'
    ),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'birthday price 1': {
    message: _t('{price} for your first month of a subscription to Coursera Plus.'),
    valueType: VALUE_TYPES.price,
  },
  'birthday price 2': {
    message: _t('Then {price}. Cancel anytime.'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  sharpen: {
    message: _t('Save {price} and sharpen your skills with Coursera Plus!'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  saveForFirst: {
    message: _t('{price} (save {saving}) for your first month of a subscription to Coursera Plus'),
    valueType: VALUE_TYPES.price,
    priceAndSaving: true,
  },
  afterFirstMonth: {
    message: _t(
      'After the first month, this subscription auto-renews monthly for {price}/month, with the option to cancel anytime'
    ),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  fall2002header: {
    message: _t('Be the new career you with {price} off Coursera Plus.'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  hundredOff: {
    message: _t('Ends Soon: Get {price} off a year of Coursera Plus'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  // Evergreen Promotion
  'Unlock new opportunities with {discountPrice} off for a year of Coursera Plus': {
    message: _t('Unlock new opportunities with {price} off for a year of Coursera Plus'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'Evergreen promo: Automatically renews on annual [date]...': {
    message: _t(
      'Automatically renews on annual basis for {price}/year (plus applicable taxes), unless canceled. Cancel anytime in account settings.'
    ),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  // TODO(mawa): Define a new valueType called `formattedText`.
  // Make this new type dynamically support copy that has links in it.
  'Terms apply': {
    message: _t('Terms apply.'),
    valueType: VALUE_TYPES.termsOfUseLink,
  },
  'Terms for Promotional Offers apply.': {
    message: _t('Terms for Promotional Offers apply.'),
    valueType: VALUE_TYPES.termsOfUseForPromosLink,
  },
  'Join 77% of surveyd learners on Coursera': {
    message: _t(''), // Formatted string will be set later on using FormattedMessage
    valueType: VALUE_TYPES.formattedSurveyedResponsesCopy,
  },
  '77% of learners report career benefits': {
    message: _t(''), // Formatted string will be set later on using FormattedMessage
    valueType: VALUE_TYPES.formattedSurveyedResponsesCopyVersion2,
  },
  '2023 Coursera Learner Outcomes Report': {
    message: _t(''), // Formatted string will be set later on using FormattedMessage
    valueType: VALUE_TYPES.courseraLearnerOutcomesReport,
  },
  'Looking to land your first job...': {
    message: '',
    valueType: VALUE_TYPES.learnerFirstJobMotivation,
  },
  'Join the 89% of learners who reported career benefits': {
    message: '',
    valueType: VALUE_TYPES.formattedSurveyedResponsesJoin89Percent,
  },
  'Join the 91% of learners who reported career benefits': {
    message: '',
    valueType: VALUE_TYPES.formattedSurveyedResponsesJoin91Percent,
  },
  'Join the 95% of learners on Coursera who...': {
    message: '',
    valueType: VALUE_TYPES.formattedSurveyedResponsesCopyQ32023,
  },
  '<strikethrough>original price</strikethrough>': {
    message: _t('{price}'),
    valueType: VALUE_TYPES.strikethroughValue,
    blockDiscount: true,
  },
  '{price} for 1 year (save {saving})': {
    message: _t('{price} for 1 year (save {saving})'),
    valueType: VALUE_TYPES.price,
    priceAndSaving: true,
  },
  'Reach new goals. Get Coursera Plus for {discountPrice} off.': {
    message: _t('Reach new goals. Get Coursera Plus for {price} off.'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'Your next step starts now. Gain career-defining skills...': {
    message: _t(
      'Your next step starts now. Gain career-defining skills on Coursera Plus with access to over {numberOfProductsNoPlusSign} programs from leading institutions like Google, Microsoft, IBM, Meta, and more—now {price} off the annual price for a limited time only.*'
    ),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'About Archi:': {
    message: _t(''), // Formatted string will be set later on using FormattedMessage
    valueType: VALUE_TYPES.aboutArchi,
  },
  'Last chance! Get Coursera Plus...': {
    message: _t('Last chance! Get Coursera Plus {price} off your first year'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'Claim this special limited-time offer by December 31, 2024...': {
    message: _t(
      `*Claim this special limited-time offer by January 31, 2025 11:59 PM UTC. Valid for new Coursera Plus subscribers only, limited to one per person. Cannot be used in conjunction with other offers. Offer not available to India residents. Coursera reserves the right to modify or cancel the promotion at any time. Discount applied at checkout. Automatically renews on an annual basis for {price}/year (plus applicable taxes), unless canceled. Cancel anytime in account settings`
    ),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'Now is the time! Get Coursera Plus...': {
    message: _t(`Now's the time! Get Coursera Plus for {price} off your first year`),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'Jump back into learning with one of our best offers of the year': {
    message: _t(
      `Jump back into learning with one of our best offers of the year! Gain career-defining skills on Coursera Plus with access to over {numberOfProductsNoPlusSign} programs from leading institutions like Google, Microsoft, IBM, Meta, and more—now {price} off the annual price.*`
    ),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  'You have been taking big steps towards your career goals...': {
    message: _t(
      `You've been taking big steps towards your career goals. Now's the time to build on the progress you've made with one of our best offers of the year! Gain career-defining skills on Coursera Plus with access to over {numberOfProductsNoPlusSign} programs from leading institutions like Google, Microsoft, IBM, Meta, and more—now {price} off the annual price for a limited time only.*`
    ),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  '[Deutch] Folge dem Beispiel von 77 % der Lernenden, die von': {
    message: '',
    valueType: VALUE_TYPES.socialProofDeutchCopy,
  },
  "[French] Rejoignez les 77 % d'apprenants qui ont fait avancer leur carrière": {
    message: '',
    valueType: VALUE_TYPES.socialProofFrenchCopy,
  },
  '[LATAM] Únete al 77 % de estudiantes que obtuvieron beneficios': {
    message: '',
    valueType: VALUE_TYPES.socialProofLatamCopy,
  },
  'save {discount} on your first month!': {
    message: _t('save {price} on your first month!'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
  '(normally {full price})': {
    message: _t('(normally {price})'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'Automatically renews on a monthly basis for {full price}/month': {
    message: _t('Automatically renews on a monthly basis for {price}/month (plus applicable taxes), unless canceled.'),
    valueType: VALUE_TYPES.price,
    blockDiscount: true,
  },
  'Get {discount} off Coursera Plus': {
    message: _t('Get {price} off Coursera Plus'),
    valueType: VALUE_TYPES.price,
    showDiscountAmount: true,
  },
});

export type PropsFromPretranslatedCaller = {
  messageKey: string;
  productItemId: SubscriptionTiersProductVariant;
  isPrimary: boolean;
  shouldHideSocialProofCopy: boolean;
} & ErrorMessageFunction;

const Pretranslated = (props: PropsFromPretranslatedCaller) => {
  const { messageKey, showError, isPrimary, productItemId, shouldHideSocialProofCopy } = props;

  if (shouldHideSocialProofCopy && props.messageKey.includes('2023 Coursera Learner Outcomes Report')) {
    return null;
  }

  const message = getPretranslatedMessages(productItemId)[messageKey];
  if (!message) {
    return showError(ERROR_MESSAGES.missingTranslation, messageKey);
  }
  if ([VALUE_TYPES.price, VALUE_TYPES.strikethroughValue].includes(message.valueType)) {
    return (
      <span className={message.className}>
        <PricedPretranslated
          translatedMessage={message.message}
          isPrimary={isPrimary}
          isStrikeThroughValue={message.valueType === VALUE_TYPES.strikethroughValue}
          blockDiscount={message.blockDiscount ?? false}
          showDiscountAmount={message.showDiscountAmount ?? false}
          priceAndSaving={message.priceAndSaving ?? false}
          monthlyFromAnnualRoundedToNearestValue={message.monthlyFromAnnualRoundedToNearestValue}
          showError={showError}
        />
      </span>
    );
  }
  if (message.valueType === VALUE_TYPES.termsOfUseLink) {
    return <span className={message.className}>{getTermsOfUseLink()}</span>;
  }

  if (message.valueType === VALUE_TYPES.termsOfUseForPromosLink) {
    return (
      <span className={message.className}>{getTermsOfUseLink(_t('{termsLink} for Promotional Offers apply.'))}</span>
    );
  }

  if (message.valueType === VALUE_TYPES.formattedSurveyedResponsesCopy) {
    return <span className={message.className}>{getFormattedSurveyedResponsesCopy()}</span>;
  }

  if (message.valueType === VALUE_TYPES.formattedSurveyedResponsesCopyVersion2 && !shouldHideSocialProofCopy) {
    return <span className={message.className}>{getFormattedSurveyedResponsesCopyVersion2()}</span>;
  }

  if (message.valueType === VALUE_TYPES.courseraLearnerOutcomesReport) {
    return <span className={message.className}>{getCourseraLearnerOutcomesReport()}</span>;
  }

  if (message.valueType === VALUE_TYPES.learnerFirstJobMotivation) {
    return <span className={message.className}>{getLearnerFirstJobMotivationCopyQ32023()}</span>;
  }

  if (message.valueType === VALUE_TYPES.formattedSurveyedResponsesJoin89Percent && !shouldHideSocialProofCopy) {
    return <span className={message.className}>{getFormattedSurveyedResponsesCopyJoin(89)}</span>;
  }

  if (message.valueType === VALUE_TYPES.formattedSurveyedResponsesJoin91Percent && !shouldHideSocialProofCopy) {
    return <span className={message.className}>{getFormattedSurveyedResponsesCopyJoin(91)}</span>;
  }

  if (message.valueType === VALUE_TYPES.formattedSurveyedResponsesCopyQ32023) {
    return <span className={message.className}>{getFormattedSurveyedResponsesCopyQ32023()}</span>;
  }

  if (message.valueType === VALUE_TYPES.aboutArchi) {
    return <span className={message.className}>{getFormattedAboutArchiCopy()}</span>;
  }

  if (
    [VALUE_TYPES.socialProofLatamCopy, VALUE_TYPES.socialProofDeutchCopy, VALUE_TYPES.socialProofFrenchCopy].includes(
      message.valueType
    ) &&
    !shouldHideSocialProofCopy
  ) {
    return (
      <span className={message.className}>
        {getFormattedSurveyedResponsesCustomLanguage(message.valueType as string)}
      </span>
    );
  }

  return null;
};

export default Pretranslated;
